import React, { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Grid,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import DownloadIcon from "@mui/icons-material/Download";
import BackupIcon from "@mui/icons-material/Backup";
import RefreshIcon from "@mui/icons-material/Refresh";
import axios from "axios";
import { useTranslation } from "react-i18next";
import { useAuth } from "../../../../context";

const API = process.env.REACT_APP_API_URL;

const Backup = () => {
  const { t } = useTranslation();
  const auth = useAuth();
  const token = auth?.user?.token;
  const [loading, setLoading] = useState(false);
  const [backups, setBackups] = useState([]);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");

  const headers = token
    ? { Authorization: `Token ${token}` }
    : {};

  const fetchBackups = async () => {
    try {
      setError("");
      const response = await axios.get(`${API}/database-backup/list/`, { headers });
      const items = response.data?.results?.backups || response.data?.backups || [];
      setBackups(Array.isArray(items) ? items : []);
    } catch (fetchError) {
      setError(fetchError?.response?.data?.error || fetchError.message || "Unable to load backups.");
    }
  };

  useEffect(() => {
    fetchBackups();
  }, [token]);

  const handleCreateBackup = async () => {
    try {
      setLoading(true);
      setMessage("");
      setError("");
      const response = await axios.post(
        `${API}/database-backup/backup/`,
        { gzip: false },
        { headers }
      );
      const fileName = response.data?.file_name;
      setMessage(
        fileName
          ? `Backup created: ${fileName}`
          : "Backup created successfully."
      );
      await fetchBackups();
      if (fileName) {
        await handleDownload(fileName, true);
      }
    } catch (createError) {
      setError(createError?.response?.data?.error || createError.message || "Backup failed.");
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = async (fileName, silent = false) => {
    try {
      setError("");
      const response = await axios.get(
        `${API}/database-backup/download/${encodeURIComponent(fileName)}/`,
        {
          headers,
          responseType: "blob",
        }
      );
      const blob = new Blob([response.data], {
        type: "application/octet-stream",
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      if (!silent) {
        setMessage(`Downloading ${fileName}`);
      }
    } catch (downloadError) {
      setError(downloadError?.response?.data?.error || downloadError.message || "Download failed.");
    }
  };

  return (
    <Box sx={{ p: 3, width: "100%" }}>
      <Stack spacing={2} sx={{ mb: 3 }}>
        <Box>
          <Typography variant="h5" fontWeight="bold">
            {t("hr.backup_management", { defaultValue: "Backup Management" })}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t("hr.backup_desc", {
              defaultValue: "Create a portable archive of the database and uploaded files.",
            })}
          </Typography>
        </Box>

        {message ? <Alert severity="success">{message}</Alert> : null}
        {error ? <Alert severity="error">{error}</Alert> : null}

        <Paper sx={{ p: 2 }}>
          <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
            <Button
              variant="contained"
              startIcon={loading ? <CircularProgress size={18} color="inherit" /> : <BackupIcon />}
              onClick={handleCreateBackup}
              disabled={loading}
            >
              {t("hr.create_backup", { defaultValue: "Create Backup" })}
            </Button>
            <Button
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={fetchBackups}
            >
              Refresh
            </Button>
          </Stack>
        </Paper>
      </Stack>

      <Grid container spacing={2}>
        {backups.length > 0 ? (
          backups.map((backup) => (
            <Grid item xs={12} md={6} lg={4} key={backup.name}>
              <Card variant="outlined" sx={{ height: "100%" }}>
                <CardContent>
                  <Typography fontWeight="bold" sx={{ wordBreak: "break-word" }}>
                    {backup.name}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {backup.modified_time}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    {(Number(backup.size) / (1024 * 1024)).toFixed(2)} MB
                  </Typography>
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<DownloadIcon />}
                    onClick={() => handleDownload(backup.name)}
                  >
                    Download
                  </Button>
                </CardContent>
              </Card>
            </Grid>
          ))
        ) : (
          <Grid item xs={12}>
            <Paper sx={{ p: 3 }}>
              <Typography color="text.secondary">
                {t("hr.no_backup_files", { defaultValue: "No backup files available" })}
              </Typography>
            </Paper>
          </Grid>
        )}
      </Grid>
    </Box>
  );
};

export default Backup;
